import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { updateReviewQueue } from "@/requests/internal-products/update-review-queue";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import type { ReviewQueueItem, ReviewQueueUpdateRequest } from "@onsemble/shared/types/internal";

interface ReviewEditModalProps {
  item: ReviewQueueItem;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReviewEditModal({ item, onClose, onSuccess }: ReviewEditModalProps) {
  const { toast } = useToast();
  const [action, setAction] = useState<"approve" | "reject">("approve");

  const { register, handleSubmit, formState: { errors: _errors } } = useForm({
    defaultValues: {
      name: "",
      description: "",
      aiDescription: "",
      classification1: "",
      classification2: "",
      classification3: "",
      classification4: "",
      classification5: "",
      classification6: "",
      searchKeywords: "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ReviewQueueUpdateRequest) =>
      updateReviewQueue(item.id, data),
    onSuccess: () => {
      toast({
        title: action === "approve" ? "Product approved" : "Product rejected",
        description: `Review queue item has been ${action}d`,
      });
      onSuccess();
    },
    onError: (error) => {
      toastApiError(toast, error, "Update failed");
    },
  });

  const onSubmit = (formData: any) => {
    // Only send non-empty fields
    const updates: any = { action };
    Object.keys(formData).forEach((key) => {
      if (formData[key]) {
        updates[key] = formData[key];
      }
    });

    updateMutation.mutate(updates);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Product</DialogTitle>
          <DialogDescription>
            Review and correct product enrichment data before approval
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Product Info */}
          <div className="space-y-2">
            <div>
              <Label className="text-muted-foreground">Product Name</Label>
              <p className="font-medium">{item.productName}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">SKU</Label>
              <p className="font-medium">{item.sku || "None"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Flagged Reason</Label>
              <p className="font-medium">{item.reason.replace(/_/g, " ")}</p>
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h3 className="font-semibold">Correct Product Data (optional)</h3>

            <div className="space-y-2">
              <Label htmlFor="name">Product Name</Label>
              <Input
                id="name"
                placeholder="Leave empty to keep existing"
                {...register("name")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Leave empty to keep existing"
                rows={3}
                {...register("description")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="aiDescription">AI Description</Label>
              <Textarea
                id="aiDescription"
                placeholder="Leave empty to keep existing"
                rows={3}
                {...register("aiDescription")}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="classification1">Classification 1</Label>
                <Input
                  id="classification1"
                  placeholder="Level 1"
                  {...register("classification1")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="classification2">Classification 2</Label>
                <Input
                  id="classification2"
                  placeholder="Level 2"
                  {...register("classification2")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="classification3">Classification 3</Label>
                <Input
                  id="classification3"
                  placeholder="Level 3"
                  {...register("classification3")}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="classification4">Classification 4</Label>
                <Input
                  id="classification4"
                  placeholder="Level 4"
                  {...register("classification4")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="classification5">Classification 5</Label>
                <Input
                  id="classification5"
                  placeholder="Level 5"
                  {...register("classification5")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="classification6">Classification 6</Label>
                <Input
                  id="classification6"
                  placeholder="Level 6"
                  {...register("classification6")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="searchKeywords">Search Keywords</Label>
              <Input
                id="searchKeywords"
                placeholder="Comma-separated keywords"
                {...register("searchKeywords")}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              onClick={() => setAction("reject")}
              disabled={updateMutation.isPending}
            >
              Reject
            </Button>
            <Button
              type="submit"
              onClick={() => setAction("approve")}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Approve"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
